import { Environment, Network, RecordSource, Store } from "relay-runtime";
import { trackEvent } from "./utils/clientEvents";
import { readQuoteResumeToken } from "./utils/quoteResumeToken";
// Owner of the "liveGuestToken" storage scheme (durable localStorage slot +
// the legacy sessionStorage read it migrates from). A dependency-free leaf
// module, so importing it here adds no cycle and no weight.
import {
  getGuestToken,
  getWatchToken,
} from "./components/livestream/liveSession";

// GraphQL operations that legitimately error in the unauthenticated path
// (the public quote funnel runs the patient-auth query on every page load
// and gets back an "unauthenticated" error — that's the not-signed-in state,
// not a real error). Filter these out at the capture site.
const GRAPHQL_ERROR_DENYLIST = new Set<string>([
  "PatientAuthQueryQuery",
]);

// Booking <-> estimate guardrail refusals (docs/payments/31_BOOKING_ESTIMATE_
// GUARDRAILS.md; codes from server/services/quoteGuardrailService.ts). They are
// normal UI branches - the component turns each into a decision prompt - not
// client errors, so a response whose errors are ALL guardrail refusals is not
// recorded as graphql_error. Kept local (not imported from
// components/admin/shared/guardrailPrompts) so this module never pulls antd.
const EXPECTED_REFUSAL_CODES = new Set<string>([
  "BOOK_ON_PRICED_QUOTE",
  "PATIENT_HAS_UNPRICED_BOOKING",
  "ESTIMATE_ON_COMPLETED_QUOTE",
]);

// On the admin dashboard an expired or revoked session causes the server to
// return a 403 JSON body ({"message":"User not authorized."}) instead of a
// valid GraphQL response. Parsing that as JSON and handing it to Relay triggers
// the root ErrorBoundary ("Something went wrong.") rather than showing the
// login screen. Intercept 401/403 here: dispatch a DOM event so
// AuthenticationProvider can clear the user and render <Login> without a full
// page reload.
//
// The bundle is served under PUBLIC_URL (`/app/quote`) in staging/prod, so
// admin pathnames there are `/app/quote/admin/...`. The old `^/admin` anchor
// only matched in local dev (served at the root), leaving BOTH auth-expiry
// intercepts below dead code in production — evidence: an expired session
// flooding UNAUTHENTICATED NotificationsBellCountQuery graphql_errors (317 in
// one week from one session) instead of bouncing to login, the exact flood
// this code's own comment says it prevents. The patient portal and quote
// funnel live under `/app/quote/patient-portal` and `/app/quote/quote` — no
// `/admin` segment — so the widened match can't catch them.
const ADMIN_PATH_RE = /(^|\/)admin(\/|$)/;

function dispatchAuthExpired(): void {
  window.dispatchEvent(new CustomEvent("admin-auth-expired"));
}

// Clear the admin session and hand Relay a synthetic GraphQL error body so it
// doesn't crash while <Login> renders. Used by both expiry paths: the HTTP
// 401/403 interceptor and the resolver-level UNAUTHENTICATED envelope error.
function authExpiredResult() {
  dispatchAuthExpired();
  return {
    errors: [
      {
        message: "Session expired. Please log in again.",
        extensions: { code: "UNAUTHENTICATED" },
      },
    ],
  };
}

// Turn a non-GraphQL HTTP response into a GraphQL-SHAPED result instead of
// letting `response.json()` throw.
//
// WHY: the chain below used to end in a bare `return response.json()`. When the
// body is not JSON — Express's default HTML error page, the EC2 reverse proxy's
// plain-text "Bad Gateway", an ELB/Cloudflare interstitial, an HTML login
// redirect — JSON.parse throws a raw SyntaxError out of the network layer.
// Relay has no error envelope to work with at that point, so it propagates to
// React Router's stock boundary and the operator sees
// "Unexpected Application Error! JSON.parse: unexpected character at line 1
// column 1" — a message that names the parser, not the outage, and offers no
// way back. Handing Relay a well-formed `{data, errors}` instead means the
// failure lands on the app's OWN error surfaces (the route errorElement, the
// query error state) with a message that says what actually happened.
//
// This is a LAST RESORT, not a parser: a legitimate JSON body — including a
// JSON error body, including one with an HTTP error status — is returned
// completely unchanged, so nothing downstream (the UNAUTHENTICATED check, the
// graphql_error tracking) can be starved of a real result by this branch.
const BODY_PREVIEW_CHARS = 160;

async function readJsonOrShapedError(response: Response): Promise<any> {
  // Read as TEXT first — the body can only be consumed once, so json() would
  // leave nothing to quote in the preview when it throws.
  const body = await response.text();
  const contentType = response.headers.get("content-type") || "";
  const looksJson = /\bjson\b/i.test(contentType);

  if (looksJson) {
    try {
      return JSON.parse(body);
    } catch {
      // Advertised as JSON but isn't — a truncated response, or a proxy that
      // replaced the body and kept the header. Falls through to the envelope.
    }
  }

  return {
    data: null,
    errors: [
      {
        message: `Unexpected server response (HTTP ${response.status})`,
        extensions: {
          code: "BAD_RESPONSE",
          status: response.status,
          // Whitespace-collapsed and hard-capped: an HTML error page is mostly
          // indentation, and the first line of it ("<!DOCTYPE html>", "Bad
          // Gateway", "502 nginx") is the whole diagnostic value.
          bodyPreview: body
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, BODY_PREVIEW_CHARS),
        },
      },
    ],
  };
}

const fetchWithRetry = async (
  url: string,
  options: RequestInit,
  retries = 2,
): Promise<Response> => {
  for (let i = 0; i <= retries; i++) {
    try {
      const response = await fetch(url, options);
      return response;
    } catch (error) {
      if (i === retries) throw error;
      // Brief pause before retry to let the browser establish a new connection
      await new Promise((r) => setTimeout(r, 500));
    }
  }
  throw new Error("Unreachable");
};

const fetchQuery = (operation: any, variables: any) => {
  // Patient-portal session first; else a livestream guest session
  // ("liveGuestToken", minted by joinLiveAsGuest for /live — a DURABLE
  // localStorage token since 2026-09-03, read through liveSession.ts so the
  // legacy sessionStorage slot still migrates). Both ride this same Bearer
  // header — the server tells them apart by the JWT's `role` claim (see
  // server/routes/patient-auth-routes.ts). The portal token still WINS: a
  // signed-in patient is never demoted to a guest session.
  const token = localStorage.getItem("token") || getGuestToken();

  // Quote resume access token rides its OWN header, never Authorization/Bearer,
  // so it can coexist with (and never clobber) the patient-portal JWT above.
  // The server only honors it on the quote(id) resolver, gated by quoteId match.
  // See utils/quoteResumeToken.ts + server/services/quoteAccessTokenService.ts.
  const quoteResumeToken = readQuoteResumeToken();

  // Livestream WATCH token (Group J) — the anonymous watcher's `liveWatch` JWT.
  // Its OWN header, never Authorization/Bearer, so it can coexist with (and
  // never clobber) a portal or guest session on a device that holds both: the
  // two are read from different context slots and verified by different
  // functions server-side. It authenticates nobody — the server honors it on
  // the two live chat READS only, and only when its `eventId` claim matches the
  // broadcast being asked for, so sending it on every request (including for
  // another event, or long after that stream ended) buys nothing and costs a
  // header.
  const liveWatchToken = getWatchToken();

  // A query is safe to re-issue. A mutation is not: when fetch rejects (connection
  // reset mid-response, DNS blip) the client cannot know whether the resolver ran,
  // so a retry can apply the write twice (a second booking note, a second SMS, a
  // second payment intent). Zero retries for mutations — the surface that issued
  // it shows the network error once, same as any failed mutation today.
  const retries = operation.operationKind === "mutation" ? 0 : 2;

  return fetchWithRetry(
    "graphql",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: token ? `Bearer ${token}` : "", // Include the JWT token in the Authorization header
        ...(quoteResumeToken ? { "X-Quote-Token": quoteResumeToken } : {}),
        ...(liveWatchToken ? { "X-Live-Watch": liveWatchToken.token } : {}),
      },
      body: JSON.stringify({
        query: operation.text,
        variables,
      }),
    },
    retries,
  )
    .then((response) => {
      // Intercept 401/403 on admin paths before attempting to parse the body
      // as GraphQL. The server sends a plain JSON 403 for expired/missing
      // admin sessions which is NOT a valid GraphQL envelope — passing it to
      // Relay would trigger the root ErrorBoundary. Instead signal the
      // AuthenticationProvider to clear the user and render <Login>.
      if (
        (response.status === 401 || response.status === 403) &&
        ADMIN_PATH_RE.test(window.location.pathname)
      ) {
        return authExpiredResult();
      }
      // Everything that is NOT an admin auth expiry: parse defensively. A real
      // JSON body (success or GraphQL errors) comes back untouched.
      return readJsonOrShapedError(response);
    })
    .then((result) => {
      // Expired/absent admin session surfaced as a resolver-level GraphQL
      // error (HTTP 200) rather than an HTTP 401/403 — see the server's
      // customFormatErrorFn, which tags genuine session-gate failures with
      // extensions.code === "UNAUTHENTICATED". The HTTP-level interceptor
      // above can't catch these (the resolver's resShim absorbs the 403), so
      // without this the admin sits on a half-broken dashboard whose
      // auth-gated queries silently fail — and the notifications bell, which
      // polls the count every 30s, floods graphql_error forever. Bounce to
      // login here, mirroring the HTTP-403 branch, and DON'T record it as an
      // error (it's an expected expired-session state, not a bug).
      if (
        ADMIN_PATH_RE.test(window.location.pathname) &&
        result?.errors?.some(
          (e: any) => e?.extensions?.code === "UNAUTHENTICATED",
        )
      ) {
        return authExpiredResult();
      }
      if (
        result?.errors?.length &&
        !GRAPHQL_ERROR_DENYLIST.has(operation.name) &&
        !result.errors.every((e: any) =>
          EXPECTED_REFUSAL_CODES.has(e?.extensions?.code),
        )
      ) {
        try {
          trackEvent("graphql_error", {
            operationName: operation.name,
            operationKind: operation.operationKind,
            errors: result.errors.slice(0, 5).map((e: any) => ({
              message: typeof e?.message === "string" ? e.message.slice(0, 500) : undefined,
              path: e?.path,
              code: e?.extensions?.code,
            })),
          });
        } catch {
          /* never let analytics break the app */
        }
      }
      return result;
    });
};

const environment = new Environment({
  network: Network.create(fetchQuery),
  store: new Store(new RecordSource()),
});

export default environment;
